import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, setCsrfToken, setUnauthorisedHandler } from './api';
import type { Role, Right } from '@inbrief/shared';

export interface CurrentUser {
  userId: string;
  organisationId: string;
  practiceId: string | null;
  email: string;
  name: string;
  role: Role;
  organisationName: string;
  planId: 'core' | 'complete';
}

interface AuthState {
  user: CurrentUser | null;
  rights: Record<Right, boolean>;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const NO_RIGHTS: Record<Right, boolean> = {
  extraction: false, comms: false, translation: false, settings: false,
};

const AuthContext = createContext<AuthState>({
  user: null, rights: NO_RIGHTS, loading: true,
  signIn: async () => {}, signOut: async () => {},
});

interface MeResponse {
  /** Null when signed out — that is a valid answer, not an error. */
  user: CurrentUser | null;
  rights: Record<Right, boolean> | null;
  csrfToken: string | null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [rights, setRights] = useState<Record<Right, boolean>>(NO_RIGHTS);
  const [loading, setLoading] = useState(true);

  const apply = (data: MeResponse) => {
    setCsrfToken(data.csrfToken ?? '');
    setUser(data.user);
    setRights(data.rights ?? NO_RIGHTS);
  };

  useEffect(() => {
    setUnauthorisedHandler(() => {
      setUser(null);
      setRights(NO_RIGHTS);
      setCsrfToken('');
    });
    api.get<MeResponse>('/api/auth/me')
      .then(apply)
      .catch(() => {/* not signed in — the sign-in screen handles it */})
      .finally(() => setLoading(false));
  }, []);

  const signIn = async (email: string, password: string) => {
    apply(await api.post<MeResponse>('/api/auth/login', { email, password }));
  };

  const signOut = async () => {
    await api.post('/api/auth/logout').catch(() => {});
    setUser(null);
    setRights(NO_RIGHTS);
    setCsrfToken('');
  };

  return (
    <AuthContext.Provider value={{ user, rights, loading, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

/**
 * Greys out what this role cannot do. The server enforces the same matrix on
 * every route — this is a courtesy so people are not offered a button that
 * will refuse them, not a security control.
 */
export function useRight(right: Right): boolean {
  return useAuth().rights[right];
}
